// Übersicht aller Workflow-Berichte (/workflows/berichte, Runde 10): eine Zeile je Workflow mit Eingeschrieben, Aktiv, Fertig,
// Ziel erreicht, Abgebrochen, Fehler und den Quoten. Ein Klick auf die Zeile öffnet den Bericht des Workflows im selben Zeitraum.
import { Link, useNavigate } from "react-router-dom";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useSeitenKopf } from "../daten/seite";
import { relativ, zeitpunktLang } from "../werkzeuge/zeit";
import { useLaden } from "./daten";
import { WORKFLOW_STATUS } from "./hilfen";
import { prozent } from "./teil2Hilfen";
import { workflows2Api } from "./teil2Api";
import { ZeitraumWahl, useZeitraum } from "./ZeitraumWahl";
import "./workflows2.css";

export default function BerichteUebersicht() {
  useSeitenKopf([{ label: "Workflows", to: "/workflows" }, { label: "Berichte" }]);
  const navigate = useNavigate();
  const [zeitraum, setZeitraum] = useZeitraum(30);
  const g = useLaden(() => workflows2Api.uebersicht(zeitraum.von, zeitraum.bis), [zeitraum.von, zeitraum.bis], 30000);
  const zeilen = g.daten?.data ?? [];
  const summe = zeilen.reduce(
    (a, z) => ({ ein: a.ein + z.eingeschrieben, aktiv: a.aktiv + z.aktiv, fertig: a.fertig + z.fertig, ziel: a.ziel + z.ziel, ab: a.ab + z.abgebrochen, fehler: a.fehler + z.fehler }),
    { ein: 0, aktiv: 0, fertig: 0, ziel: 0, ab: 0, fehler: 0 },
  );
  const bericht = (id: string) => navigate(`/workflows/${encodeURIComponent(id)}/bericht?von=${zeitraum.von}&bis=${zeitraum.bis}`);

  return (
    <div className="liste-seite wf-liste wf-bericht">
      <div className="liste-kopf">
        <Link to="/workflows" className="wf-kopf__zurueck" title="Zu allen Workflows">
          <Icon name="chevron-links" groesse={14} />
          Workflows
        </Link>
        <h1 className="liste-kopf__titel">Berichte</h1>
      </div>
      <div className="liste-leiste wf-liste__leiste">
        <ZeitraumWahl zeitraum={zeitraum} onChange={setZeitraum} />
      </div>
      {g.fehler && !g.daten ? <FehlerBox fehler={g.fehler} onNochmal={() => void g.neuLaden()} /> : null}
      {g.laedt && !g.daten ? (
        <Laden />
      ) : !zeilen.length ? (
        <div className="leer">
          <div className="leer__titel">Noch keine Workflows</div>
          <div className="leer__text">Sobald es Workflows gibt, steht hier ihr Bericht.</div>
        </div>
      ) : (
        <div className="tabelle-rahmen">
          <table className="tabelle wf-bericht__tabelle wf-bericht__tabelle--liste">
            <thead>
              <tr>
                <th>Workflow</th>
                <th className="wf-zahl">Eingeschrieben</th>
                <th className="wf-zahl">Aktiv</th>
                <th className="wf-zahl">Fertig</th>
                <th className="wf-zahl">Ziel erreicht</th>
                <th className="wf-zahl">Abgebrochen</th>
                <th className="wf-zahl">Fehler</th>
                <th className="wf-zahl" title="Ziel erreicht, von allen Eingeschriebenen">Ziel-Quote</th>
                <th className="wf-zahl">Fehlerquote</th>
                <th>Letzte Aktivität</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.workflow_id} onClick={() => bericht(z.workflow_id)}>
                  <td className="wf-zelle-name">
                    <Link to={`/workflows/${encodeURIComponent(z.workflow_id)}/bericht?von=${zeitraum.von}&bis=${zeitraum.bis}`} onClick={(e) => e.stopPropagation()} className="wf-liste__name">
                      <Icon name="blitz" groesse={14} />
                      <span className="gekuerzt">{z.name}</span>
                    </Link>
                    {z.status !== "aktiv" ? <span className="leise wf-bericht__status"> · {WORKFLOW_STATUS[z.status as keyof typeof WORKFLOW_STATUS] ?? z.status}</span> : null}
                  </td>
                  <td className="wf-zahl">{z.eingeschrieben}</td>
                  <td className="wf-zahl">{z.aktiv}</td>
                  <td className="wf-zahl">{z.fertig}</td>
                  <td className="wf-zahl">{z.hat_ziele || z.ziel ? z.ziel : <span className="leise">–</span>}</td>
                  <td className="wf-zahl">{z.abgebrochen}</td>
                  <td className={`wf-zahl${z.fehler ? " text-gefahr" : ""}`}>{z.fehler}</td>
                  <td className="wf-zahl">{z.hat_ziele ? prozent(z.ziel_quote) : <span className="leise">–</span>}</td>
                  <td className="wf-zahl">{z.fehler ? prozent(z.fehler_quote) : <span className="leise">–</span>}</td>
                  <td className="leise" title={zeitpunktLang(z.letzter_lauf_at)}>{z.letzter_lauf_at ? relativ(z.letzter_lauf_at) : "noch nie"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Gesamt</th>
                <td className="wf-zahl">{summe.ein}</td>
                <td className="wf-zahl">{summe.aktiv}</td>
                <td className="wf-zahl">{summe.fertig}</td>
                <td className="wf-zahl">{summe.ziel}</td>
                <td className="wf-zahl">{summe.ab}</td>
                <td className="wf-zahl">{summe.fehler}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
